/*
 * HOK PDP – click-to-open image lightbox (vanilla JS, no libraries, no hover zoom).
 *
 * Hooks into the existing Splide gallery (#main-carousel, rendered by snippets/media-product.liquid and
 * snippets/product-media-slider.liquid). The gallery is never rebuilt or modified.
 *  - Click an image slide  -> full-screen popup opens on exactly that image (high-resolution copy).
 *  - Zoom: + / - buttons, mouse wheel, double-click / double-tap, pinch; drag (mouse or touch) to move; reset button.
 *  - Navigate: prev / next buttons, ArrowLeft / ArrowRight, swipe (when not zoomed), thumbnail strip.
 *  - Close: X button, Escape, click on the dark background. Page scroll is locked while open.
 *  - Videos / 3D models / external video are not part of the lightbox.
 *
 * The image list is read from the gallery at the moment of the click, so variant changes
 * (updateVariantGallery / restoreOriginalGallery / media slider) are always reflected.
 * Listeners on #main-carousel are attached once (guarded), and document-level listeners exist only while the popup is open.
 */
(function () {
  'use strict';

  var HI_WIDTH = 2000;
  var THUMB_WIDTH = 160;
  var MAX_SCALE = 5;
  var STEP = 0.5;

  var root;
  var lb = null; // lightbox DOM refs
  var items = [];
  var index = 0;
  var isOpen = false;
  var lastFocus = null;

  var s = 1, tx = 0, ty = 0; // zoom state
  var fitW = 0, fitH = 0; // displayed (scale 1) size of the image
  var pointers = new Map();
  var gesture = null; // { type:'pan'|'pinch'|'swipe', ... }
  var raf = 0;
  var lastTap = 0;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- image list ---------------- */

  function resize(url, w) {
    if (!url) return '';
    try {
      var u = new URL(url, location.href);
      if (/cdn\.shopify\.com|\/cdn\/shop\//.test(u.href)) {
        u.searchParams.delete('height');
        u.searchParams.delete('crop');
        u.searchParams.set('width', w);
      }
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  function isImageSlide(slide) {
    if (!slide || slide.querySelector('video, model-viewer, iframe')) return false;
    var type = slide.getAttribute('data-media-type');
    if (type && type !== 'image') return false;
    return !!slide.querySelector('img');
  }

  function collect(clickedSlide) {
    var list = [];
    var start = 0;
    var slides = root.querySelectorAll('.splide__list > .splide__slide');
    for (var i = 0; i < slides.length; i++) {
      if (!isImageSlide(slides[i])) continue;
      var img = slides[i].querySelector('img');
      var src = img.currentSrc || img.src;
      if (!src) continue;
      if (slides[i] === clickedSlide) start = list.length;
      list.push({
        slideIndex: i,
        low: src,
        hi: img.getAttribute('data-zoom-src') || resize(src, HI_WIDTH),
        thumb: resize(src, THUMB_WIDTH),
        alt: img.getAttribute('alt') || document.title,
        ar: img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1
      });
    }
    return { list: list, start: start };
  }

  /* ---------------- DOM ---------------- */

  function icon(path) {
    return '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + path + '</svg>';
  }

  function btn(cls, label, svg) {
    return '<button type="button" class="hok-lb__btn ' + cls + '" aria-label="' + label + '" title="' + label + '">' + svg + '</button>';
  }

  function build() {
    if (lb) return;
    var el = document.createElement('div');
    el.className = 'hok-lb';
    el.id = 'hok-lightbox';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Product image viewer');
    el.hidden = true;
    el.innerHTML =
      '<div class="hok-lb__bar">' +
      '<span class="hok-lb__count" aria-live="polite"></span>' +
      '<div class="hok-lb__tools">' +
      btn('hok-lb__zoom-out', 'Zoom out', icon('<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3M8 11h6"/>')) +
      '<span class="hok-lb__level" aria-live="polite">100%</span>' +
      btn('hok-lb__zoom-in', 'Zoom in', icon('<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3M8 11h6M11 8v6"/>')) +
      btn('hok-lb__reset', 'Reset zoom', icon('<path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5"/>')) +
      btn('hok-lb__close', 'Close', icon('<path d="M18 6L6 18M6 6l12 12"/>')) +
      '</div></div>' +
      '<div class="hok-lb__stage"><img class="hok-lb__img" alt="" draggable="false" decoding="async"></div>' +
      btn('hok-lb__nav hok-lb__prev', 'Previous image', icon('<path d="M15 18l-6-6 6-6"/>')) +
      btn('hok-lb__nav hok-lb__next', 'Next image', icon('<path d="M9 18l6-6-6-6"/>')) +
      '<div class="hok-lb__thumbs" role="tablist" aria-label="Product images"></div>';
    document.body.appendChild(el);
    lb = {
      el: el,
      stage: el.querySelector('.hok-lb__stage'),
      img: el.querySelector('.hok-lb__img'),
      count: el.querySelector('.hok-lb__count'),
      level: el.querySelector('.hok-lb__level'),
      thumbs: el.querySelector('.hok-lb__thumbs'),
      prev: el.querySelector('.hok-lb__prev'),
      next: el.querySelector('.hok-lb__next'),
      zin: el.querySelector('.hok-lb__zoom-in'),
      zout: el.querySelector('.hok-lb__zoom-out')
    };

    // one-time listeners on the lightbox itself
    el.querySelector('.hok-lb__close').addEventListener('click', close);
    el.querySelector('.hok-lb__reset').addEventListener('click', function () { resetZoom(true); });
    lb.zin.addEventListener('click', function () { zoomBy(STEP * 2, null, true); });
    lb.zout.addEventListener('click', function () { zoomBy(-STEP * 2, null, true); });
    lb.prev.addEventListener('click', function () { go(index - 1); });
    lb.next.addEventListener('click', function () { go(index + 1); });
    lb.thumbs.addEventListener('click', function (e) {
      var b = e.target.closest('button[data-i]');
      if (b) go(+b.getAttribute('data-i'));
    });
    lb.stage.addEventListener('wheel', onWheel, { passive: false });
    lb.stage.addEventListener('pointerdown', onDown);
    lb.stage.addEventListener('pointermove', onMove);
    lb.stage.addEventListener('pointerup', onUp);
    lb.stage.addEventListener('pointercancel', onUp);
    lb.stage.addEventListener('dblclick', onDbl);
    lb.img.addEventListener('animationend', function () { lb.img.classList.remove('is-in-next', 'is-in-prev'); });
    lb.img.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  /* ---------------- zoom / layout ---------------- */

  function stageSize() {
    var r = lb.stage.getBoundingClientRect();
    return { w: r.width, h: r.height };
  }

  function layoutImage() {
    var st = stageSize();
    var it = items[index];
    var ar = it.ar || 1;
    fitW = Math.min(st.w, st.h * ar);
    fitH = fitW / ar;
    lb.img.style.width = fitW + 'px';
    lb.img.style.height = fitH + 'px';
  }

  function clampPan() {
    var st = stageSize();
    var mx = Math.max(0, (fitW * s - st.w) / 2);
    var my = Math.max(0, (fitH * s - st.h) / 2);
    tx = Math.max(-mx, Math.min(mx, tx));
    ty = Math.max(-my, Math.min(my, ty));
  }

  function apply(animate) {
    clampPan();
    lb.img.classList.toggle('is-animated', !!animate && !reduceMotion);
    lb.img.style.transform = 'translate3d(' + tx + 'px,' + ty + 'px,0) scale(' + s + ')';
    lb.stage.classList.toggle('is-zoomed', s > 1.001);
    lb.level.textContent = Math.round(s * 100) + '%';
    lb.zin.disabled = s >= MAX_SCALE - 0.001;
    lb.zout.disabled = s <= 1.001;
  }

  function schedule() {
    if (raf) return;
    raf = requestAnimationFrame(function () { raf = 0; apply(false); });
  }

  // Zoom to new scale keeping the point (cx, cy) (client coords; default stage centre) fixed.
  function setScale(ns, cx, cy, animate) {
    ns = Math.max(1, Math.min(MAX_SCALE, ns));
    var r = lb.stage.getBoundingClientRect();
    var px = (cx == null ? r.left + r.width / 2 : cx) - r.left - r.width / 2;
    var py = (cy == null ? r.top + r.height / 2 : cy) - r.top - r.height / 2;
    var qx = (px - tx) / s;
    var qy = (py - ty) / s;
    s = ns;
    tx = px - qx * s;
    ty = py - qy * s;
    if (s === 1) { tx = 0; ty = 0; }
    apply(animate);
  }

  function zoomBy(delta, p, animate) {
    setScale(s + delta, p && p.x, p && p.y, animate);
  }

  function resetZoom(animate) {
    s = 1; tx = 0; ty = 0;
    if (lb) apply(animate);
  }

  /* ---------------- input ---------------- */

  function onWheel(e) {
    e.preventDefault();
    var factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0018));
    setScale(s * factor, e.clientX, e.clientY, false);
  }

  function onDbl(e) {
    if (e.pointerType === 'touch') return;
    toggleZoom(e.clientX, e.clientY);
  }

  function toggleZoom(x, y) {
    if (s > 1.001) resetZoom(true);
    else setScale(2.5, x, y, true);
  }

  function dist() {
    var p = Array.from(pointers.values());
    return Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) || 1;
  }

  function center() {
    var p = Array.from(pointers.values());
    return { x: (p[0].x + p[1].x) / 2, y: (p[0].y + p[1].y) / 2 };
  }

  function onDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    lb.stage.setPointerCapture && lb.stage.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, t: Date.now() });
    lb.img.classList.remove('is-animated');
    if (pointers.size === 2) {
      var c = center();
      var r = lb.stage.getBoundingClientRect();
      gesture = {
        type: 'pinch', d0: dist(), s0: s,
        qx: (c.x - r.left - r.width / 2 - tx) / s,
        qy: (c.y - r.top - r.height / 2 - ty) / s
      };
    } else if (pointers.size === 1) {
      gesture = { type: s > 1.001 ? 'pan' : 'swipe', moved: false, startTx: tx, startTy: ty };
    }
  }

  function onMove(e) {
    var p = pointers.get(e.pointerId);
    if (!p || !gesture) return;
    var dx = e.clientX - p.x;
    var dy = e.clientY - p.y;
    p.x = e.clientX;
    p.y = e.clientY;
    if (gesture.type === 'pinch' && pointers.size === 2) {
      var r = lb.stage.getBoundingClientRect();
      var c = center();
      s = Math.max(0.9, Math.min(MAX_SCALE, (gesture.s0 * dist()) / gesture.d0));
      tx = c.x - r.left - r.width / 2 - s * gesture.qx;
      ty = c.y - r.top - r.height / 2 - s * gesture.qy;
      schedule();
    } else if (gesture.type === 'pan') {
      if (Math.abs(p.x - p.sx) + Math.abs(p.y - p.sy) > 4) gesture.moved = true;
      tx += dx;
      ty += dy;
      lb.stage.classList.add('is-dragging');
      schedule();
    } else if (gesture.type === 'swipe') {
      if (Math.abs(p.x - p.sx) + Math.abs(p.y - p.sy) > 6) gesture.moved = true;
      if (gesture.moved) {
        // follow the finger a little for feedback
        lb.img.style.transform = 'translate3d(' + (p.x - p.sx) * 0.6 + 'px,0,0)';
      }
    }
  }

  function onUp(e) {
    var p = pointers.get(e.pointerId);
    if (!p) return;
    pointers.delete(e.pointerId);
    lb.stage.classList.remove('is-dragging');
    if (gesture && gesture.type === 'pinch') {
      if (pointers.size < 2) {
        if (s < 1.05) resetZoom(true);
        else apply(true);
        gesture = null;
        // remaining finger must lift before a new gesture starts
        pointers.clear();
      }
      return;
    }
    var g = gesture;
    gesture = null;
    if (!g) return;
    var dx = p.x - p.sx;
    var dy = p.y - p.sy;
    if (g.type === 'swipe') {
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.3 && items.length > 1) {
        go(index + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
        return;
      }
      lb.img.classList.add('is-animated');
      lb.img.style.transform = 'translate3d(0,0,0) scale(1)';
      if (!g.moved) {
        // tap: backdrop closes, double-tap on touch zooms
        if (isBackdrop(e)) { close(); return; }
        if (e.pointerType === 'touch') {
          var now = Date.now();
          if (now - lastTap < 300) { lastTap = 0; toggleZoom(e.clientX, e.clientY); }
          else lastTap = now;
        }
      }
    } else if (g.type === 'pan' && !g.moved) {
      if (isBackdrop(e)) { close(); return; }
      if (e.pointerType === 'touch') {
        var n = Date.now();
        if (n - lastTap < 300) { lastTap = 0; toggleZoom(e.clientX, e.clientY); }
        else lastTap = n;
      }
    }
    apply(true);
  }

  // pointer capture makes e.target the stage, so hit-test the real element under the pointer
  function isBackdrop(e) {
    var t = document.elementFromPoint(e.clientX, e.clientY);
    return !t || t === lb.stage || t === lb.el;
  }

  function onKey(e) {
    if (!isOpen) return;
    switch (e.key) {
      case 'Escape': e.preventDefault(); close(); break;
      case 'ArrowLeft': e.preventDefault(); go(index - 1); break;
      case 'ArrowRight': e.preventDefault(); go(index + 1); break;
      case '+': case '=': e.preventDefault(); zoomBy(STEP * 2, null, true); break;
      case '-': case '_': e.preventDefault(); zoomBy(-STEP * 2, null, true); break;
      case '0': e.preventDefault(); resetZoom(true); break;
      case 'Tab': trapFocus(e); break;
    }
  }

  function trapFocus(e) {
    var f = Array.prototype.filter.call(lb.el.querySelectorAll('button:not([disabled])'), function (b) { return b.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && (document.activeElement === first || !lb.el.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (document.activeElement === last || !lb.el.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
  }

  function onResize() {
    if (!isOpen) return;
    layoutImage();
    apply(false);
  }

  /* ---------------- show / navigate ---------------- */

  function preload(i) {
    var it = items[(i + items.length) % items.length];
    if (it && !it.loaded && !it.loading) {
      it.loading = true;
      var im = new Image();
      im.decoding = 'async';
      im.onload = function () { it.loaded = true; };
      im.src = it.hi;
    }
  }

  function show(i, dir) {
    index = (i + items.length) % items.length;
    var it = items[index];
    resetZoom(false);
    layoutImage();
    lb.img.alt = it.alt;
    lb.img.classList.remove('is-animated');
    lb.img.style.transform = '';
    lb.img.src = it.loaded ? it.hi : it.low; // instant: the gallery copy is already cached
    lb.img.classList.remove('is-in-next', 'is-in-prev');
    if (dir && !reduceMotion) {
      void lb.img.offsetWidth;
      lb.img.classList.add(dir > 0 ? 'is-in-next' : 'is-in-prev');
    }
    apply(false);
    if (!it.loaded) {
      var token = index;
      var im = new Image();
      im.decoding = 'async';
      im.onload = function () {
        it.loaded = true;
        var swap = function () { if (isOpen && token === index) lb.img.src = it.hi; };
        if (im.decode) im.decode().then(swap, swap); else swap();
      };
      im.src = it.hi;
    }
    lb.count.textContent = index + 1 + ' / ' + items.length;
    var multi = items.length > 1;
    lb.prev.hidden = lb.next.hidden = !multi;
    lb.thumbs.hidden = !multi;
    Array.prototype.forEach.call(lb.thumbs.children, function (b, n) {
      var on = n === index;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on && b.scrollIntoView) {
        try { lb.thumbs.scrollTo({ left: b.offsetLeft - lb.thumbs.clientWidth / 2 + b.clientWidth / 2, behavior: reduceMotion ? 'auto' : 'smooth' }); } catch (e) {}
      }
    });
    if (multi) { preload(index + 1); preload(index - 1); }
  }

  function go(i, dir) {
    if (!isOpen || items.length < 2) return;
    var d = dir || (i > index ? 1 : -1);
    show(i, d);
  }

  function open(slide) {
    var data = collect(slide);
    if (!data.list.length) return;
    build();
    items = data.list;
    lastFocus = document.activeElement;
    lb.thumbs.innerHTML = items
      .map(function (it, n) {
        return '<button type="button" role="tab" data-i="' + n + '" aria-label="Image ' + (n + 1) + '"><img src="' + it.thumb + '" alt="" width="64" height="64" loading="lazy" decoding="async"></button>';
      })
      .join('');
    lb.el.hidden = false;
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--hok-lb-sbw', sbw + 'px');
    document.documentElement.classList.add('hok-lb-open');
    isOpen = true;
    show(data.start);
    requestAnimationFrame(function () {
      lb.el.classList.add('is-open');
      layoutImage();
      apply(false);
      lb.el.querySelector('.hok-lb__close').focus({ preventScroll: true });
    });
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    pointers.clear();
    gesture = null;
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('resize', onResize);
    lb.el.classList.remove('is-open');
    var done = function () {
      if (isOpen) return;
      lb.el.hidden = true;
      lb.img.removeAttribute('src');
      document.documentElement.classList.remove('hok-lb-open');
      document.documentElement.style.removeProperty('--hok-lb-sbw');
    };
    if (reduceMotion) done(); else setTimeout(done, 220);
    // keep the page gallery on the image the customer was looking at
    try {
      var it = items[index];
      if (it && window.mainSplide && window.mainSplide.index !== it.slideIndex) window.mainSplide.go(it.slideIndex);
    } catch (e) {}
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) {} }
  }

  /* ---------------- gallery hook ---------------- */

  function init() {
    root = document.getElementById('main-carousel');
    if (!root || root.__hokLb) return;
    root.__hokLb = true;

    var down = null;
    root.addEventListener('pointerdown', function (e) { down = { x: e.clientX, y: e.clientY }; }, { passive: true });
    root.addEventListener('click', function (e) {
      var img = e.target.closest ? e.target.closest('.splide__slide img') : null;
      if (!img || !root.contains(img)) return;
      var slide = img.closest('.splide__slide');
      if (!isImageSlide(slide)) return;
      if (down && Math.hypot(e.clientX - down.x, e.clientY - down.y) > 8) return; // it was a slider drag
      open(slide);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.HokPDPLightbox = { init: init, open: function () { var sl = root && root.querySelector('.splide__slide.is-active'); if (sl) open(sl); }, close: close };
})();
